import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { DownloadCloud, FileVideo } from 'lucide-react';
import {
  fetchAvailableDates,
  fetchDetections,
  fetchSummary,
  getDownloadAllUrl,
  getDownloadDateUrl
} from '../api';
import DateSelector from '../components/DateSelector';
import DetectionLog from '../components/DetectionLog';
import EvidenceModal from '../components/EvidenceModal';
import StatsSummary from '../components/StatsSummary';

export default function History() {
  const [dateOptions, setDateOptions] = useState([{ key: 'today', label: 'Today' }]);
  const [selectedDate, setSelectedDate] = useState('today');
  const [summary, setSummary] = useState(null);
  const [detections, setDetections] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [evidenceId, setEvidenceId] = useState(null);

  const refreshHistory = async (dateKey = selectedDate) => {
    setLoadingLog(true);
    try {
      const [summaryRes, detectionRes] = await Promise.all([
        fetchSummary(dateKey),
        fetchDetections(dateKey, 'all', 300)
      ]);
      setSummary(summaryRes.data);
      setDetections(detectionRes.data?.detections || []);
    } catch (err) {
      toast.error('Failed to load history data');
    } finally {
      setLoadingLog(false);
    }
  };

  useEffect(() => {
    async function loadDates() {
      try {
        const response = await fetchAvailableDates();
        const dates = response.data?.dates || [];
        const sorted = [...dates].sort((a, b) => {
          if (a.key === 'today') return -1;
          if (b.key === 'today') return 1;
          return 0;
        });
        setDateOptions(sorted.length ? sorted : [{ key: 'today', label: 'Today' }]);
      } catch (err) {
        toast.error('Failed to fetch available dates');
      }
    }
    loadDates();
  }, []);

  useEffect(() => {
    refreshHistory(selectedDate);
  }, [selectedDate]);

  const downloadDate = selectedDate === 'today'
    ? new Date().toLocaleDateString('en-GB').replace(/\//g, '-')
    : selectedDate;

  return (
    <div className="page-grid">
      <section className="control-bar control-bar--history">
        <DateSelector
          options={dateOptions}
          value={selectedDate}
          onChange={setSelectedDate}
        />
        <a href={getDownloadDateUrl(downloadDate)} download={`FSQA_Log_${downloadDate}.json`} className="ghost-btn">
          <DownloadCloud size={15} />
          Download Selected Date (JSON)
        </a>
        <a href={getDownloadAllUrl()} download="FSQA_All_Detections.json" className="ghost-btn">
          <DownloadCloud size={15} />
          Download All (JSON)
        </a>
        <Link to="/history/saved-videos" className="ghost-btn">
          <FileVideo size={15} />
          Historical Saved Videos
        </Link>
      </section>

      <StatsSummary summary={summary} />

      <DetectionLog
        detections={detections}
        onViewEvidence={setEvidenceId}
        onResolve={() => null}
        onRefresh={() => refreshHistory(selectedDate)}
        canResolve={false}
        loading={loadingLog}
      />

      {evidenceId && (
        <EvidenceModal detectionId={evidenceId} onClose={() => setEvidenceId(null)} />
      )}
    </div>
  );
}
